<template>
  <v-main>
    <loader :loading="loading"></loader>
    <v-container fluid>
      <div class="breadcrumbs-container">
        <Breadcrumbs />
      </div>
      <div class="flex flex-col space-y-6">
        <!-- Hero Card -->
        <v-card class="cl-hero-card" elevation="3">
          <div class="cl-hero-header">
            <div class="cl-hero-header-content">
              <div class="cl-hero-logo">
                <v-icon color="white" size="30">mdi-clipboard-check-outline</v-icon>
              </div>
              <div>
                <div class="cl-hero-title">Claims Management</div>
                <div class="cl-hero-subtitle">
                  Define and track claims for your security assurance case
                </div>
              </div>
            </div>
          </div>
          <div class="cl-workflow-section">
            <div class="cl-section-header">
              <v-icon color="primary" size="22">mdi-information-outline</v-icon>
              <span class="text-subtitle-1 font-weight-bold primary--text">Claim Lifecycle</span>
            </div>
            <v-row>
              <v-col cols="12" md="4">
                <div class="cl-metric-card">
                  <div
                    class="cl-metric-icon"
                    style="background: linear-gradient(135deg, #e3f2fd, #bbdefb);"
                  >
                    <v-icon color="primary">mdi-plus-circle-outline</v-icon>
                  </div>
                  <div class="cl-metric-label">Create</div>
                  <div class="cl-metric-desc">Define claims with clear titles and descriptions</div>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="cl-metric-card">
                  <div
                    class="cl-metric-icon"
                    style="background: linear-gradient(135deg, #fff3e0, #ffe0b2);"
                  >
                    <v-icon color="orange">mdi-graph</v-icon>
                  </div>
                  <div class="cl-metric-label">Sub-Claims</div>
                  <div class="cl-metric-desc">Break down claims into verifiable sub-claims</div>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="cl-metric-card">
                  <div
                    class="cl-metric-icon"
                    style="background: linear-gradient(135deg, #e8f5e9, #c8e6c9);"
                  >
                    <v-icon color="success">mdi-check-decagram</v-icon>
                  </div>
                  <div class="cl-metric-label">Score</div>
                  <div class="cl-metric-desc">
                    Evaluate comprehensiveness &amp; sufficiency metrics
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-card>

        <!-- Back Navigation -->
        <v-btn
          class="cl-back-btn"
          outlined
          color="primary"
          @click="$router.push({ name: 'SacBuilder' })"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          {{ $t('backToRequirements') }}
        </v-btn>

        <div class="cl-content-wrapper">
          <!-- Search Bar -->
          <v-card class="cl-search-bar-container" elevation="2">
            <div class="cl-search-bar-header">
              <v-icon color="white" size="20" class="mr-2">mdi-magnify</v-icon>
              <span class="white--text text-subtitle-2 font-weight-bold">Search &amp; Filter</span>
            </div>
            <div class="cl-search-bar-body">
              <div class="flex flex-row items-center space-x-4">
                <v-select
                  v-model="searchField"
                  :items="searchFields"
                  item-text="text"
                  item-value="value"
                  :label="$t('search-field')"
                  hide-details
                  outlined
                  dense
                  class="mb-4 search-field"
                  style="max-width: 200px;"
                ></v-select>
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  :label="$t('search')"
                  hide-details
                  class="mb-4 flex-grow-1"
                  outlined
                  dense
                  @keyup.enter="applySearch"
                />
                <v-btn class="bg-primary-500" @click="enableCreateDialog()">
                  <v-icon class="mr-2 text-primary-800">mdi-plus</v-icon>
                  {{ $t('claims.create') }}
                </v-btn>
              </div>
            </div>
          </v-card>

          <!-- Data Table Card -->
          <v-card class="cl-table-card" elevation="2">
            <div class="cl-table-header">
              <v-icon color="white" size="20" class="mr-2">mdi-table</v-icon>
              <span class="white--text text-subtitle-2 font-weight-bold">Claims</span>
            </div>
            <v-data-table
              :headers="getHeaders"
              :items="claims"
              :search="search"
              :custom-filter="customFilter"
              class="elevation-1 certifai-table"
              :loading="loading"
              :items-per-page="5"
              :footer-props="{
                showFirstLastPage: true,
                firstIcon: 'mdi-arrow-collapse-left',
                lastIcon: 'mdi-arrow-collapse-right',
                prevIcon: 'mdi-arrow-left',
                nextIcon: 'mdi-arrow-right',
                disableItemsPerPage: false,
                itemsPerPageText: getItemsPerPageText,
                showCurrentPage: true,
              }"
              must-sort
              :sort-desc="true"
            >
              <template v-slot:[`item.subClaimsCount`]="{ item }">
                <v-chip color="blue" outlined small>
                  {{ item.subClaimsCount || 0 }} {{ $t('subClaims.title') }}
                </v-chip>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip :color="statusChipColor(item.status)" outlined dark>
                  <span class="text-capitalize">{{ $t(item.status) }}</span>
                </v-chip>
              </template>
              <template v-slot:[`item.comprehensivenessScore`]="{ item }">
                <div
                  v-if="
                    item.comprehensivenessScore !== null &&
                      item.comprehensivenessScore !== undefined
                  "
                >
                  <v-chip
                    small
                    :color="getScoreColor(item.comprehensivenessScore)"
                    dark
                    class="font-weight-bold"
                  >
                    {{ (item.comprehensivenessScore * 100).toFixed(1) }}%
                  </v-chip>
                </div>
                <span v-else class="text-caption grey--text">Not scored</span>
              </template>
              <template v-slot:[`item.sufficiencyScore`]="{ item }">
                <div v-if="item.sufficiencyScore !== null && item.sufficiencyScore !== undefined">
                  <v-chip
                    small
                    :color="getScoreColor(item.sufficiencyScore)"
                    dark
                    class="font-weight-bold"
                  >
                    {{ (item.sufficiencyScore * 100).toFixed(1) }}%
                  </v-chip>
                </div>
                <span v-else class="text-caption grey--text">Not scored</span>
              </template>
              <template v-slot:[`item.score`]="{ item }">
                <v-progress-circular
                  v-if="item.score !== null && item.score !== undefined"
                  :value="item.score"
                  :color="getScoreColor(item.score / 100)"
                  :size="40"
                  :width="5"
                  class="font-weight-bold"
                >
                  {{ item.score }}
                </v-progress-circular>
                <span v-else class="text-caption grey--text">N/A</span>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      class="g-action-btn g-action-edit"
                      v-on="on"
                      v-bind="attrs"
                      @click="handleClaimClicked(item)"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('edit') }}</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      class="g-action-btn g-action-info"
                      v-on="on"
                      v-bind="attrs"
                      @click="previewSubClaims(item)"
                    >
                      <v-icon small>mdi-graph</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('preview') }} {{ $t('subClaims.title') }}</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      class="g-action-btn g-action-info"
                      v-on="on"
                      v-bind="attrs"
                      @click="viewClaimDetails(item)"
                    >
                      <v-icon small>mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>View Scoring Details</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      class="g-action-btn g-action-delete"
                      v-on="on"
                      v-bind="attrs"
                      @click="enableDeleteDialog(item)"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('delete.message') }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card>

          <v-dialog v-model="deleteClicked" max-width="500">
            <v-card class="cl-dialog-card">
              <v-card-title>{{ $t('delete.confirm') }}</v-card-title>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="gray" text @click="deleteClicked = false">{{ $t('cancel') }}</v-btn>
                <v-btn color="red" text @click="deleteClaim">{{ $t('delete.message') }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-dialog v-model="editClicked" max-width="500">
            <v-card class="cl-dialog-card">
              <v-card-title>
                {{ $t('sac.editClaimTitle') }} {{ this.inScopeRequirement.title }}
              </v-card-title>
              <v-card-text>
                <v-form ref="editForm" v-model="editFormValid">
                  <v-text-field
                    v-model="editItem.title"
                    :label="$t('claims.headers.title')"
                    outlined
                    dense
                    :rules="[rules.required]"
                    required
                  ></v-text-field>
                  <v-textarea
                    v-model="editItem.description"
                    :hint="$t('claims.claimDescriptionHint')"
                    :label="$t('claims.headers.description')"
                    auto-grow
                    rows="2"
                    max-rows="6"
                    outlined
                    dense
                    :rules="[rules.required]"
                    required
                  ></v-textarea>
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="gray" text @click="handleCancelEdit()">
                  {{ $t('cancel') }}
                </v-btn>
                <v-btn
                  :disabled="!this.editItem.title || !this.editItem.description"
                  color="green"
                  text
                  @click="editClaim()"
                >
                  {{ $t('editHeader') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-dialog v-model="createClicked" max-width="500">
            <v-card class="cl-dialog-card">
              <v-card-title>
                {{ $t('sac.createClaimTitle') }} {{ this.inScopeRequirement.title }}
              </v-card-title>
              <v-card-text>
                <v-form ref="createForm" v-model="createFormValid">
                  <v-text-field
                    v-model="claim.title"
                    :label="$t('claims.headers.title')"
                    outlined
                    dense
                    :rules="[rules.required, rules.duplicateName]"
                    :error-messages="
                      duplicateNameExists ? 'Claim with this name already exists' : ''
                    "
                    required
                    @input="checkDuplicateName"
                  ></v-text-field>
                  <v-textarea
                    v-model="claim.description"
                    :hint="$t('claims.claimDescriptionHint')"
                    :label="$t('claims.headers.description')"
                    auto-grow
                    rows="2"
                    max-rows="6"
                    outlined
                    dense
                    :rules="[rules.required]"
                    required
                  ></v-textarea>
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="gray" text @click="cancelCreate">
                  {{ $t('cancel') }}
                </v-btn>
                <v-btn
                  :disabled="
                    !createFormValid || !claim.title || !claim.description || duplicateNameExists
                  "
                  color="green"
                  text
                  @click="createClaim()"
                >
                  {{ $t('sac.createClaim') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Claim Details Dialog -->
          <v-dialog v-model="detailsDialog" max-width="700">
            <v-card class="cl-dialog-card">
              <v-card-title class="headline primary white--text">
                <v-icon color="white" class="mr-2">mdi-clipboard-text-outline</v-icon>
                Claim Details
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text v-if="selectedClaim" class="pt-4">
                <v-row>
                  <v-col cols="12">
                    <div class="detail-section">
                      <div class="detail-label">Title</div>
                      <div class="detail-value">{{ selectedClaim.title }}</div>
                    </div>
                  </v-col>

                  <v-col cols="12">
                    <div class="detail-section">
                      <div class="detail-label">Description</div>
                      <div class="detail-value">{{ selectedClaim.description }}</div>
                    </div>
                  </v-col>

                  <v-col cols="6">
                    <div class="detail-section">
                      <div class="detail-label">Status</div>
                      <v-chip :color="statusChipColor(selectedClaim.status)" outlined dark>
                        <span class="text-capitalize">{{ selectedClaim.status }}</span>
                      </v-chip>
                    </div>
                  </v-col>
                </v-row>

                <v-divider class="my-4"></v-divider>

                <div class="scoring-section">
                  <div class="section-title mb-3">
                    <v-icon color="primary" class="mr-2">mdi-check-decagram</v-icon>
                    <span class="font-weight-bold">Exclaim Scorer Metrics</span>
                  </div>

                  <v-row
                    v-if="
                      selectedClaim.comprehensivenessScore !== null ||
                        selectedClaim.sufficiencyScore !== null
                    "
                  >
                    <v-col cols="6">
                      <v-card outlined>
                        <v-card-text>
                          <div class="text-center">
                            <div class="text-caption grey--text mb-2">Comprehensiveness</div>
                            <v-progress-circular
                              v-if="selectedClaim.comprehensivenessScore !== null"
                              :value="selectedClaim.comprehensivenessScore * 100"
                              :color="getScoreColor(selectedClaim.comprehensivenessScore)"
                              :size="80"
                              :width="8"
                              class="font-weight-bold"
                            >
                              {{ (selectedClaim.comprehensivenessScore * 100).toFixed(1) }}%
                            </v-progress-circular>
                            <div v-else class="grey--text">Not scored</div>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <v-col cols="6">
                      <v-card outlined>
                        <v-card-text>
                          <div class="text-center">
                            <div class="text-caption grey--text mb-2">Sufficiency</div>
                            <v-progress-circular
                              v-if="selectedClaim.sufficiencyScore !== null"
                              :value="selectedClaim.sufficiencyScore * 100"
                              :color="getScoreColor(selectedClaim.sufficiencyScore)"
                              :size="80"
                              :width="8"
                              class="font-weight-bold"
                            >
                              {{ (selectedClaim.sufficiencyScore * 100).toFixed(1) }}%
                            </v-progress-circular>
                            <div v-else class="grey--text">Not scored</div>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <v-col cols="12" v-if="selectedClaim.scoredAt">
                      <v-alert type="info" text dense>
                        <div class="d-flex align-center">
                          <v-icon class="mr-2">mdi-clock-outline</v-icon>
                          <span>Scored on: {{ formatFullDate(selectedClaim.scoredAt) }}</span>
                        </div>
                      </v-alert>
                    </v-col>

                    <v-col cols="12" v-if="selectedClaim.scoringPremise">
                      <div class="detail-section">
                        <div class="detail-label">Scoring Premise</div>
                        <div class="detail-value text-body-2">
                          {{ selectedClaim.scoringPremise }}
                        </div>
                      </div>
                    </v-col>
                  </v-row>

                  <v-alert v-else type="warning" outlined class="mt-3">
                    <div class="d-flex align-center">
                      <v-icon class="mr-2">mdi-alert</v-icon>
                      <span>This claim has not been scored with Exclaim Scorer yet.</span>
                    </div>
                  </v-alert>
                </div>

                <v-divider class="my-4"></v-divider>

                <v-row>
                  <v-col cols="6">
                    <div class="detail-section">
                      <div class="detail-label">Created By</div>
                      <div class="detail-value">{{ selectedClaim.username }}</div>
                    </div>
                  </v-col>

                  <v-col cols="6">
                    <div class="detail-section">
                      <div class="detail-label">Created At</div>
                      <div class="detail-value">{{ selectedClaim.createdAt }}</div>
                    </div>
                  </v-col>

                  <v-col cols="12" v-if="selectedClaim.updatedAt">
                    <div class="detail-section">
                      <div class="detail-label">Last Updated</div>
                      <div class="detail-value">{{ selectedClaim.updatedAt }}</div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-divider></v-divider>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" text @click="detailsDialog = false">Close</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </div>
    </v-container>
  </v-main>
</template>

<script>
import util from '@/mixins/util.js';
import claimsApi from '../api/claimsApi';
import subClaimsApi from '../api/subClaimsApi';
import EventBus from '@/helpers/event-bus.js';
import Loader from '@/components/helpers/Loader';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';
import { debounce } from 'lodash';

export default {
  name: 'ComponentList',
  components: {
    Loader,
    Breadcrumbs,
  },
  mixins: [util],
  computed: {
    getItemsPerPageText() {
      const t = this.$t.bind(this);
      return t('claims.table-footer');
    },
    getHeaders() {
      const t = this.$t.bind(this);
      return [
        {
          text: t('claims.headers.id'),
          value: 'id',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '5%',
        },
        {
          text: t('claims.headers.title'),
          value: 'title',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '12%',
        },
        {
          text: t('claims.headers.description'),
          value: 'description',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '15%',
        },
        {
          text: t('subClaims.count'),
          value: 'subClaimsCount',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '8%',
        },
        {
          text: t('claims.headers.createdBy'),
          value: 'username',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('claims.headers.createdAt'),
          value: 'createdAt',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('claims.headers.updatedAt'),
          value: 'updatedAt',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('claims.headers.status'),
          value: 'status',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '8%',
        },
        {
          text: t('claims.headers.actions'),
          value: 'actions',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
      ];
    },
    searchFields() {
      return [
        { text: this.$t('search-field-all'), value: 'all' },
        { text: this.$t('claims.headers.id'), value: 'id' },
        { text: this.$t('claims.headers.title'), value: 'title' },
        { text: this.$t('claims.headers.description'), value: 'description' },
        { text: this.$t('claims.headers.createdBy'), value: 'username' },
        { text: this.$t('claims.headers.createdAt'), value: 'createdAt' },
        { text: this.$t('claims.headers.updatedAt'), value: 'updatedAt' },
        { text: this.$t('claims.headers.status'), value: 'status' },
      ];
    },
  },
  data() {
    return {
      inScopeRequirement: JSON.parse(localStorage.getItem('inScopeRequirement')),
      loading: false,
      search: null,
      searchField: 'all',
      deleteClicked: false,
      deleteItem: null,
      createClicked: false,
      editClicked: false,
      editItem: {},
      claim: {
        title: '',
        description: '',
        socRequirementLink: {},
      },
      claims: [],
      duplicateNameExists: false,
      checkingDuplicate: false,
      createFormValid: false,
      editFormValid: false,
      rules: {
        required: value => !!value || 'Required.',
        duplicateName: () => !this.duplicateNameExists || 'Claim with this name already exists.',
      },
      detailsDialog: false,
      selectedClaim: null,
    };
  },
  created() {
    this.$emit('loading', true);
    this.loading = true;
    this.fetchClaims();
    this.$emit('loading', false);
    this.loading = false;
    this.debouncedCheckDuplicate = debounce(this.checkDuplicateName, 500);
  },
  methods: {
    async fetchClaims() {
      try {
        const { data: rawClaims } = await claimsApi.all(this.inScopeRequirement.id);
        this.claims = rawClaims;
        console.log(this.claims);
        // Fetch subclaims count for each claim
        await this.fetchSubClaimsCount();
      } catch (error) {
        if (error.response.status === 400) {
          EventBus.$emit('snackbar', {
            text: this.$t('errorList.error-404'),
            color: 'grey',
          });
        }
      }
    },
    async fetchSubClaimsCount() {
      try {
        for (let claim of this.claims) {
          try {
            const { data: subClaims } = await subClaimsApi.all(claim.id);
            this.$set(claim, 'subClaimsCount', subClaims.length || 0);
          } catch (error) {
            this.$set(claim, 'subClaimsCount', 0);
          }
        }
      } catch (error) {
        console.error('Error fetching subclaims count:', error);
      }
    },
    async createClaim() {
      // Double-check for duplicates before proceeding
      if (this.duplicateNameExists) {
        EventBus.$emit('snackbar', {
          text: 'Claim with this name already exists',
          color: 'error',
        });
        return;
      }

      this.loading = true;
      this.claim.socRequirementLink = JSON.parse(localStorage.getItem('inScopeRequirement'));
      try {
        await claimsApi.create(this.claim);
        EventBus.$emit('snackbar', {
          text: this.$t('create.successClaim'),
          color: 'green',
        });
        await this.fetchClaims();
      } catch (error) {
        console.error('Error occurred during creation:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      } finally {
        this.createClicked = false;
        this.resetClaim();
        this.loading = false;
      }
    },
    async editClaim() {
      // Check for duplicates when renaming
      if (this.editItem.title !== this.originalTitle) {
        try {
          const response = await claimsApi.checkDuplicateName(this.editItem.title);
          if (response.data) {
            EventBus.$emit('snackbar', {
              text: 'Claim with this name already exists',
              color: 'error',
            });
            return;
          }
        } catch (error) {
          console.error('Error checking duplicate name:', error);
        }
      }

      this.loading = true;
      try {
        await claimsApi.update(this.editItem);
        EventBus.$emit('snackbar', {
          text: this.$t('editSuccessClaim'),
          color: 'green',
        });
        await this.fetchClaims();
      } catch (error) {
        console.error('Error occurred during update:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      } finally {
        this.editClicked = false;
        this.loading = false;
      }
    },
    async checkDuplicateName() {
      if (!this.claim.title || this.claim.title.trim() === '') {
        this.duplicateNameExists = false;
        return;
      }

      // Don't check if we're editing an existing claim with the same name
      if (this.editItem && this.editItem.id) {
        if (this.editItem.title === this.claim.title) {
          this.duplicateNameExists = false;
          return;
        }
      }

      this.checkingDuplicate = true;
      try {
        const response = await claimsApi.checkDuplicateName(this.claim.title);
        this.duplicateNameExists = response.data;

        // Force form validation to update
        this.$nextTick(() => {
          if (this.$refs.createForm) {
            this.$refs.createForm.validate();
          }
        });
      } catch (error) {
        console.error('Error checking duplicate name:', error);
        this.duplicateNameExists = false;
      } finally {
        this.checkingDuplicate = false;
      }
    },
    cancelCreate() {
      this.createClicked = false;
      this.resetClaim();
    },
    resetClaim() {
      this.claim.title = '';
      this.claim.description = '';
      this.claim.socRequirementLink = {};
      this.duplicateNameExists = false;
      if (this.$refs.createForm) {
        this.$refs.createForm.resetValidation();
      }
    },
    async deleteClaim() {
      this.loading = true;
      try {
        await claimsApi.delete(this.deleteItem.id);
        // Find the index of the deleted item and remove it from the array
        const index = this.claims.findIndex(claim => claim.id === this.deleteItem.id);
        if (index !== -1) {
          this.claims.splice(index, 1);
        }
        EventBus.$emit('snackbar', {
          text: this.$t('delete.success'),
          color: 'green',
        });
      } catch (error) {
        console.error('Error occurred during deletion:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      } finally {
        this.deleteClicked = false;
        this.loading = false;
      }
    },
    handleClaimClicked(item) {
      this.editItem = item;
      this.editClicked = true;
      this.originalTitle = item.title; // Save original title for comparison
      localStorage.setItem('claim', JSON.stringify(item));
    },
    handleCancelEdit() {
      this.fetchClaims();
      this.editClicked = false;
    },
    enableDeleteDialog(item) {
      this.deleteItem = item;
      this.deleteClicked = true;
    },
    enableCreateDialog() {
      this.createClicked = true;
      this.duplicateNameExists = false;
      this.claim = {
        title: '',
        description: '',
        socRequirementLink: {},
      };
      this.$nextTick(() => {
        if (this.$refs.createForm) {
          this.$refs.createForm.resetValidation();
        }
      });
    },
    byteToUint8Array(byteArray) {
      var uint8Array = new Uint8Array(byteArray.length);
      for (var i = 0; i < uint8Array.length; i++) {
        uint8Array[i] = byteArray[i];
      }

      return uint8Array;
    },
    statusChipColor(status) {
      switch (status) {
        case 'Draft':
          return 'orange';
        case 'Pending':
          return 'primary';
        case 'Completed':
          return 'green';
        default:
          return 'primary';
      }
    },
    toggleSelection(id) {
      this.selectedComponentId = this.selectedComponentId === id ? null : id;
    },
    customFilter(value, search, item) {
      if (!search) return true;

      search = search.toString().toLowerCase();

      if (this.searchField === 'all') {
        // Search in all fields (default behavior)
        return Object.keys(item).some(key => {
          // Skip action column and non-string values
          if (key === 'actions' || key === 'raw' || item[key] === null || item[key] === undefined) {
            return false;
          }

          try {
            const itemValue = item[key].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        });
      } else {
        // Search only in the selected field
        if (item[this.searchField] !== null && item[this.searchField] !== undefined) {
          try {
            const itemValue = item[this.searchField].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        }
        return false;
      }
    },
    applySearch() {
      // This method can be used for any additional search logic if needed
      // For now it's a placeholder as the filtering happens automatically
    },
    previewSubClaims(item) {
      // Save the claim data to localStorage and navigate to SubClaims
      localStorage.setItem('claim', JSON.stringify(item));
      this.$router.push({ name: 'SubClaims' });
    },
    getScoreColor(score) {
      const percentage = score * 100;
      if (percentage >= 75) return 'success';
      if (percentage >= 50) return 'warning';
      if (percentage >= 25) return 'orange';
      return 'error';
    },
    formatScoreDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    formatFullDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleString();
    },
    viewClaimDetails(item) {
      this.selectedClaim = item;
      this.detailsDialog = true;
    },
  },
};
</script>

<style scoped>
/* ── Breadcrumbs ── */
.breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 40px;
}
.breadcrumbs-container .v-breadcrumbs {
  background: #ffffff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 800;
}

/* ── Hero Card ── */
.cl-hero-card {
  border-radius: 16px !important;
  overflow: hidden;
  margin-bottom: 8px;
  border: none !important;
}
.cl-hero-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%);
  padding: 32px 36px;
  position: relative;
  overflow: hidden;
}
.cl-hero-header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -10%;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
}
.cl-hero-header-content {
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
  z-index: 1;
}
.cl-hero-logo {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}
.cl-hero-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.02em;
}
.cl-hero-subtitle {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 2px;
}

/* ── Workflow Section ── */
.cl-workflow-section {
  padding: 28px 36px 32px;
}
.cl-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

/* ── Metric Cards ── */
.cl-metric-card {
  background: #f8f9ff;
  border: 1px solid #e8eaf6;
  border-radius: 14px;
  padding: 22px;
  text-align: center;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  height: 100%;
}
.cl-metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(26, 35, 126, 0.1);
}
.cl-metric-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
}
.cl-metric-label {
  font-weight: 700;
  font-size: 1rem;
  color: #1a237e;
  margin-bottom: 6px;
}
.cl-metric-desc {
  font-size: 0.85rem;
  color: #546e7a;
  line-height: 1.5;
}

/* ── Content Wrapper ── */
.cl-content-wrapper {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

/* ── Back Button ── */
.cl-back-btn {
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.02em;
  padding: 8px 20px !important;
  text-transform: none !important;
  font-size: 0.95rem !important;
  border: 2px solid currentColor !important;
  transition: all 0.25s ease !important;
}
.cl-back-btn:hover {
  background: #e8eaf6 !important;
  transform: translateX(-3px);
}

/* ── Search Bar ── */
.cl-search-bar-container {
  border-radius: 12px !important;
  overflow: hidden;
  margin: 16px 0;
  border: none !important;
}
.cl-search-bar-header {
  background: linear-gradient(135deg, #37474f 0%, #455a64 50%, #546e7a 100%);
  padding: 10px 20px;
  display: flex;
  align-items: center;
}
.cl-search-bar-body {
  padding: 16px 20px 8px;
}

/* ── Table Card ── */
.cl-table-card {
  border-radius: 12px !important;
  overflow: hidden;
  border: none !important;
}
.cl-table-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 100%);
  padding: 12px 20px;
  display: flex;
  align-items: center;
}

/* ── Dialog Card ── */
.cl-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}

/* ── Search Field ── */
.search-field {
  min-width: 150px;
}

/* ── Detail Section (Claim Details Dialog) ── */
.detail-section {
  margin-bottom: 16px;
}
.detail-label {
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  color: #666;
  margin-bottom: 4px;
}
.detail-value {
  font-size: 14px;
  color: #333;
}

/* ── Scoring Section ── */
.scoring-section {
  background: linear-gradient(135deg, #f5f7ff, #eef1fb);
  padding: 20px;
  border-radius: 14px;
  border: 1px solid #e8eaf6;
}
.section-title {
  display: flex;
  align-items: center;
  font-size: 16px;
}
</style>
